import { useEffect, useRef, useState } from "react";
import { Check, Minus, Plus } from "lucide-react";
import { useEditorStore } from "../store";
import { MAX_ZOOM, MIN_ZOOM, ZOOM_PRESETS, effectiveZoom, formatZoom } from "../zoom";

const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);
const MOD = isMac ? "⌘" : "Ctrl+";

/** Floating zoom buttons in the corner of the canvas. */
export function ZoomControl() {
  const zoom = useEditorStore((s) => s.zoom);
  const fitZoom = useEditorStore((s) => s.fitZoom);
  const setZoom = useEditorStore((s) => s.setZoom);
  const zoomBy = useEditorStore((s) => s.zoomBy);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const current = effectiveZoom(zoom, fitZoom);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("mousedown", close);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", close);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const choose = (value: number | "fit") => {
    setZoom(value);
    setOpen(false);
  };

  return (
    <div className="zirro-zoom" ref={ref} onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        className="zirro-ed-btn zirro-ed-btn--icon"
        onClick={() => zoomBy(-1)}
        disabled={current <= MIN_ZOOM}
        title={`Zoom out (${MOD}−)`}
        aria-label="Zoom out"
      >
        <Minus size={14} />
      </button>
      <button
        type="button"
        className="zirro-zoom__value"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Zoom ${formatZoom(current)}${zoom === "fit" ? ", fit to screen" : ""}`}
        title="Zoom options"
      >
        {formatZoom(current)}
      </button>
      <button
        type="button"
        className="zirro-ed-btn zirro-ed-btn--icon"
        onClick={() => zoomBy(1)}
        disabled={current >= MAX_ZOOM}
        title={`Zoom in (${MOD}+)`}
        aria-label="Zoom in"
      >
        <Plus size={14} />
      </button>

      {open ? (
        <div className="zirro-zoom__menu" role="menu">
          <button type="button" role="menuitemradio" aria-checked={zoom === "fit"} onClick={() => choose("fit")}>
            <span>Fit to screen</span>
            <kbd>⇧1</kbd>
            {zoom === "fit" ? <Check size={13} aria-hidden="true" /> : null}
          </button>
          {ZOOM_PRESETS.map((preset) => (
            <button
              key={preset}
              type="button"
              role="menuitemradio"
              aria-checked={zoom === preset}
              onClick={() => choose(preset)}
            >
              <span>{formatZoom(preset)}</span>
              {preset === 1 ? <kbd>{MOD}0</kbd> : null}
              {zoom === preset ? <Check size={13} aria-hidden="true" /> : null}
            </button>
          ))}
          <p className="zirro-zoom__hint">Pinch, or hold {MOD.replace("+", "")} and scroll, to zoom</p>
        </div>
      ) : null}
    </div>
  );
}
